import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useKnockClient, useNotificationStore, useNotifications } from '@knocklabs/react';
import { Badge, ClickAwayListener, Grow, Paper, Popper, Tooltip } from '@mui/material';
import { BellNotificationIcon } from '@truvhq/ui';

import { KNOCK_FEED_CHANNEL_ID } from '../lib/constants';
import { NotificationsFeed } from './NotificationsFeed';

export const NotificationBell: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const knockClient = useKnockClient();
  const feedClient = useNotifications(knockClient, KNOCK_FEED_CHANNEL_ID);
  const { metadata } = useNotificationStore(feedClient);

  useEffect(() => {
    feedClient.listenForUpdates();
    feedClient.fetch();

    return () => {
      feedClient.teardown();
    };
  }, [feedClient]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setIsOpen(false);
  };

  const hasUnread = metadata.unread_count > 0;

  return (
    <>
      <Tooltip title="Notifications" placement="right">
        <button
          ref={anchorRef}
          type="button"
          onClick={handleToggle}
          className="relative flex h-8 w-8 items-center bg-transparent justify-center rounded-full text-white transition-colors hover:bg-white/10"
          aria-label="Notifications"
        >
          <BellNotificationIcon width={20} height={20} />
          {hasUnread && (
            <Badge
              variant="dot"
              color="primary"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                '& .MuiBadge-dot': {
                  top: '8px',
                  right: '2px',
                  width: '12px',
                  border: '2px solid rgb(26 26 26 / var(--tw-bg-opacity, 1))',
                  height: '12px',
                  borderRadius: '50px',
                  backgroundColor: '#5C8EFF',
                },
              }}
            />
          )}
        </button>
      </Tooltip>

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper
              elevation={8}
              sx={{
                width: 420,
                maxHeight: 560,
                overflow: 'hidden',
                borderRadius: 2,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <NotificationsFeed bellFeedClient={feedClient} onClose={() => setIsOpen(false)} />
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};
